import { getAppConfig } from "@/utils/appManager";
import datashipsLogo from "@/assets/svgs/dataships-logo.svg";
import { Trans, useTranslation } from "react-i18next";
import { H1, H3, H4 } from "@/components/Typography";
import { Button } from "@/components/Button";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import CopyContent from "@/components/CopyContent";
import { useGetSmsOnboardingPolicyContent } from "@/requests/hooks/collect";
import ComponentError from "@/components/ComponentError";
import { useParams } from "react-router-dom";
import { MutableRefObject, useEffect, useRef } from "react";
import { useFlags } from "flagsmith/react";
import { IFeatureFlagKeys } from "@/utils/featureFlags";
import Accordion from "@/components/Accordion";
import SmsOnboardingPolicyContentLoading from "@/routes/Sms/SmsOnboardingPolicyContent/SmsOnboardingPolicyContentLoading";

import klaviyoLogo from "@/assets/svgs/klaviyo-logo.svg";
import shopifyLogo from "@/assets/svgs/shopify-logo.svg";
import attentiveLogo from "@/assets/images/attentive-logo.png";

type II18nKey =
  | "smsOnboarding.policiesContent.klaviyoInstructions.step1"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step2"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step3"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step4"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step5a"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step5b"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step5c"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step6"
  | "smsOnboarding.policiesContent.klaviyoInstructions.step7"
  | "smsOnboarding.policiesContent.shopifyInstructions.step1"
  | "smsOnboarding.policiesContent.shopifyInstructions.step2"
  | "smsOnboarding.policiesContent.shopifyInstructions.step3"
  | "smsOnboarding.policiesContent.shopifyInstructions.step4"
  | "smsOnboarding.policiesContent.shopifyInstructions.step5"
  | "smsOnboarding.policiesContent.shopifyInstructions.step6"
  | "smsOnboarding.policiesContent.shopifyInstructions.step7"
  | "smsOnboarding.policiesContent.attentiveInstructions.step1"
  | "smsOnboarding.policiesContent.attentiveInstructions.step2"
  | "smsOnboarding.policiesContent.attentiveInstructions.step3"
  | "smsOnboarding.policiesContent.attentiveInstructions.step4";

const SmsOnboardingPolicyContent = () => {
  const { website } = getAppConfig();
  const { t } = useTranslation();
  const { token } = useParams();
  const {
    data: smsOnboardingPolicyContentData,
    isPending: isPendingGetSmsOnboardingPolicyContent,
    isError: isErrorGetSmsOnboardingPolicyContent,
  } = useGetSmsOnboardingPolicyContent(token!);
  const privacyPolicySnippetARef = useRef<HTMLDivElement | null>(null);
  const privacyPolicySnippetBRef = useRef<HTMLDivElement | null>(null);
  const klaviyoTermsOfServiceRef = useRef<HTMLDivElement | null>(null);
  const shopifyTermsOfServiceRef = useRef<HTMLDivElement | null>(null);
  const snippetRef = useRef<HTMLDivElement | null>(null);
  const flags = useFlags<IFeatureFlagKeys>(["snippet-sms-enabled"]);
  const isSnippetSmsEnabled = flags?.["snippet-sms-enabled"]?.enabled;

  useEffect(() => {
    if (!smsOnboardingPolicyContentData) return;

    const {
      privacyPolicyDataProcessing,
      privacyPolicyMarketingPreferences,
      termsOfService,
    } = smsOnboardingPolicyContentData;

    if (privacyPolicySnippetARef?.current) {
      privacyPolicySnippetARef.current.innerHTML = privacyPolicyDataProcessing;
    }
    if (privacyPolicySnippetBRef?.current) {
      privacyPolicySnippetBRef.current.innerHTML =
        privacyPolicyMarketingPreferences;
    }
    if (klaviyoTermsOfServiceRef?.current) {
      klaviyoTermsOfServiceRef.current.innerHTML = termsOfService;
    }
    if (shopifyTermsOfServiceRef?.current) {
      shopifyTermsOfServiceRef.current.innerHTML = termsOfService;
    }
  }, [smsOnboardingPolicyContentData]);

  const renderLogo = () => (
    <a href={website} className="py-50 hover:opacity-75">
      <img src={datashipsLogo} alt="Dataships Logo" width={170} height={80} />
    </a>
  );

  const renderStepAndLink = (textKey: II18nKey, url?: string) => (
    <div className="mb-20">
      <Trans i18nKey={textKey}>
        zero
        <Button
          as="a"
          variant="link"
          target="_blank"
          href={url}
          icon={faCaretRight}
          iconPosition="end"
          iconClassName="text-secondary">
          one
        </Button>
      </Trans>
    </div>
  );

  const renderCodeSection = (
    text?: string,
    ref?: MutableRefObject<HTMLDivElement | null>,
    isCode?: boolean,
  ) => (
    <div className="mb-20">
      <CopyContent content={text} ref={ref} isCode={isCode} />
    </div>
  );

  const renderAttentiveInstructions = () => (
    <Accordion
      topContent={renderAccordionTopConsent({
        logo: attentiveLogo,
        title: t("smsOnboarding.policiesContent.attentiveInstructions.title"),
      })}>
      <H4 className="mb-10 font-bold md:mb-20">
        {t(
          "smsOnboarding.policiesContent.attentiveInstructions.integrationConfigTitle",
        )}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.attentiveInstructions.step1",
        `/integrations/mat-settings/attentive/configuration`,
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.attentiveInstructions.smsSetting")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.attentiveInstructions.step2",
        "https://ui.attentivemobile.com/settings/contact-card",
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.attentiveInstructions.step3",
        "https://ui.attentivemobile.com/settings/company-settings/us/general",
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.attentiveInstructions.step4",
        "https://ui.attentivemobile.com/settings/company-settings/us/messaging",
      )}
    </Accordion>
  );

  const renderKlaviyoInstructions = () => (
    <Accordion
      topContent={renderAccordionTopConsent({
        logo: klaviyoLogo,
        title: t("smsOnboarding.policiesContent.klaviyoInstructions.title"),
      })}>
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.klaviyoInstructions.senderTitle")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step1",
        "https://www.klaviyo.com/settings/sms/sender-information",
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t(
          "smsOnboarding.policiesContent.klaviyoInstructions.complianceChecksTitle",
        )}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step2",
        "https://www.klaviyo.com/settings/sms/sending-preferences",
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step3",
        "https://www.klaviyo.com/settings/sms/keyword-responses",
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step4",
        "https://www.klaviyo.com/settings/sms/terms-of-service",
      )}
      {renderCodeSection(
        smsOnboardingPolicyContentData?.termsOfService,
        klaviyoTermsOfServiceRef,
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step5a",
      )}
      {renderCodeSection(smsOnboardingPolicyContentData?.smsPrivacyPolicyUrl)}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step5b",
      )}
      {renderCodeSection(smsOnboardingPolicyContentData?.termsOfServiceUrl)}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step5c",
      )}
      {renderCodeSection(smsOnboardingPolicyContentData?.smsOptInDisclaimer)}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step6",
        "https://www.klaviyo.com/settings/account/api-keys",
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.klaviyoInstructions.newSmsListTitle")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.klaviyoInstructions.step7",
        "https://www.klaviyo.com/lists",
      )}
    </Accordion>
  );

  const renderShopifyInstructions = () => (
    <>
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.shopifyInstructions.policySettings")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.shopifyInstructions.step1",
        smsOnboardingPolicyContentData?.shopifyPolicyUrl,
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.shopifyInstructions.privacyPolicy")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.shopifyInstructions.step2",
      )}
      {renderCodeSection(
        smsOnboardingPolicyContentData?.privacyPolicyDataProcessing,
        privacyPolicySnippetARef,
      )}
      {renderStepAndLink(
        "smsOnboarding.policiesContent.shopifyInstructions.step3",
      )}
      {renderCodeSection(
        smsOnboardingPolicyContentData?.privacyPolicyMarketingPreferences,
        privacyPolicySnippetBRef,
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t("smsOnboarding.policiesContent.shopifyInstructions.termsOfService")}
      </H4>
      {renderStepAndLink(
        "smsOnboarding.policiesContent.shopifyInstructions.step4",
      )}
      {renderCodeSection(
        smsOnboardingPolicyContentData?.termsOfService,
        shopifyTermsOfServiceRef,
      )}
      {isSnippetSmsEnabled && (
        <>
          <H4 className="mb-10 md:mb-20">
            <Trans i18nKey="smsOnboarding.policiesContent.shopifyInstructions.optionalDatashipsSnippet">
              <b>zero</b>
              one
            </Trans>
          </H4>
          {renderStepAndLink(
            "smsOnboarding.policiesContent.shopifyInstructions.step5",
          )}
          {renderCodeSection(
            smsOnboardingPolicyContentData?.snippet,
            snippetRef,
            true,
          )}
        </>
      )}
      <H4 className="mb-10 font-bold md:mb-20">
        {t(
          "smsOnboarding.policiesContent.shopifyInstructions.consentCollectionChanges",
        )}
      </H4>
      <div className="inline-flex items-center">
        <div className="mb-20 mr-5">{isSnippetSmsEnabled ? "6." : "5."}</div>
        {renderStepAndLink(
          "smsOnboarding.policiesContent.shopifyInstructions.step6",
          smsOnboardingPolicyContentData?.shopifyCheckoutSettingsUrl,
        )}
      </div>
      <H4 className="mb-10 font-bold md:mb-20">
        {t(
          "smsOnboarding.policiesContent.shopifyInstructions.transactionalMessageChanges",
        )}
      </H4>

      <div className="inline-flex items-center">
        <div className="mb-20 mr-5">{isSnippetSmsEnabled ? "7." : "6."}</div>
        {renderStepAndLink(
          "smsOnboarding.policiesContent.shopifyInstructions.step7",
          smsOnboardingPolicyContentData?.shopifyThemeSettingsUrl,
        )}
      </div>
    </>
  );

  const loginButton = () => (
    <div className="mx-auto mt-40 flex flex-row justify-center">
      <Button as="link" variant="white-outline" to="/auth/login">
        {t("smsOnboarding.policiesContent.loginCTA")}
      </Button>
    </div>
  );

  const renderAccordionTopConsent = ({
    logo,
    title,
  }: {
    logo: string;
    title: string;
  }) => (
    <div className="flex flex-row items-center gap-10">
      <img src={logo} className="h-20 w-20" />
      <H4 color="secondary" className="font-bold">
        {title}
      </H4>
    </div>
  );

  const renderContent = () => {
    if (isPendingGetSmsOnboardingPolicyContent)
      return <SmsOnboardingPolicyContentLoading />;

    if (isErrorGetSmsOnboardingPolicyContent)
      return (
        <div className="rounded-sm bg-white p-50 shadow-sm">
          <ComponentError message={t("smsOnboarding.policiesContent.error")} />
        </div>
      );

    return (
      <>
        <H1 className="mb-10 text-center font-bold" color="white">
          {smsOnboardingPolicyContentData?.companyName}
        </H1>
        <H3 className="mb-20 text-center font-bold" color="white">
          {t("smsOnboarding.policiesContent.header", {
            store: smsOnboardingPolicyContentData?.companyName,
          })}
        </H3>

        {smsOnboardingPolicyContentData?.matIntegration === "ATTENTIVE" &&
          renderAttentiveInstructions()}
        {(smsOnboardingPolicyContentData?.matIntegration.includes("KLAVIYO") ||
          null === smsOnboardingPolicyContentData?.matIntegration) &&
          renderKlaviyoInstructions()}

        <div className="mt-20 md:mt-30">
          <Accordion
            defaultIsExpanded={false}
            topContent={renderAccordionTopConsent({
              logo: shopifyLogo,
              title: t(
                "smsOnboarding.policiesContent.shopifyInstructions.title",
              ),
            })}>
            {renderShopifyInstructions()}
          </Accordion>
        </div>

        {loginButton()}
      </>
    );
  };

  const renderBasicLink = ({ url, text }: { url: string; text: string }) => (
    <a className="hover:underline" target="_blank" href={url}>
      {text}
    </a>
  );

  const renderLinks = () => (
    <p className="mb-20 mt-50 text-white">
      {renderBasicLink({ url: `${website}/privacy-centre`, text: "Privacy" })} &{" "}
      {renderBasicLink({ url: `${website}/terms-of-business`, text: "Terms" })}
    </p>
  );

  const renderCopyright = () => {
    const year = new Date().getFullYear();
    return (
      <p className="mb-50 text-white">&copy; {year} Copyright Dataships</p>
    );
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-primary bg-gradient-to-br from-primary to-green p-20">
      {renderLogo()}
      <div className="mx-auto w-full max-w-[1000px]">{renderContent()}</div>
      {renderLinks()}
      {renderCopyright()}
    </div>
  );
};

export default SmsOnboardingPolicyContent;
